import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'August 23, 2026';
const CONTACT_EMAIL = 'legal@momorox.com';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    <div className="space-y-3 text-gray-400 text-sm leading-relaxed">{children}</div>
  </div>
);

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-dark-base text-gray-300">
      <div className="border-b border-white/10 bg-dark-lighter">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-black text-gradient">MomoroX</Link>
          <span className="text-gray-500 text-sm">Terms of Service</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-black text-white mb-2">Terms of Service</h1>
        <p className="text-gray-500 text-sm mb-12">Effective date: {EFFECTIVE_DATE}</p>

        <Section title="1. Agreement to Terms">
          <p>
            These Terms of Service ("Terms") govern your access to and use of the MomoroX Concierge
            platform, including our website at momorox.com, the dashboard at concierge.momorox.com,
            the embeddable chat widget, and any associated software including the WordPress plugin
            (collectively, the "Service"), operated by MomoroX, Inc. ("MomoroX", "we", "us", or "our").
          </p>
          <p>
            By creating an account, installing the WordPress plugin, or using the Service in any way,
            you agree to be bound by these Terms. If you are using the Service on behalf of a dental
            practice or other organization, you represent that you have authority to bind that organization
            to these Terms.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            MomoroX Concierge is an AI-powered patient engagement platform for dental practices.
            The Service enables dental practices ("Practices") to deploy an AI chat assistant on their
            websites to answer patient questions, collect appointment requests, and optionally integrate
            with practice management software for real-time scheduling.
          </p>
          <p>
            The Service includes:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>An embeddable JavaScript chat widget for practice websites</li>
            <li>A dashboard for managing the widget, conversations, and appointment requests</li>
            <li>AI-powered responses trained on practice-specific content</li>
            <li>Optional integrations with Open Dental, Google Calendar, and other scheduling software</li>
            <li>A WordPress plugin for simplified widget installation</li>
            <li>Email notifications for new appointment requests</li>
          </ul>
        </Section>

        <Section title="3. Accounts and Registration">
          <p>
            To use the Service, you must create an account at concierge.momorox.com. You agree to:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Promptly notify us of any unauthorized access to your account</li>
            <li>Accept responsibility for all activity that occurs under your account</li>
          </ul>
          <p>
            You must be at least 18 years old to create an account. We reserve the right to refuse
            registration or cancel accounts at our discretion.
          </p>
        </Section>

        <Section title="4. Acceptable Use">
          <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Use the Service for any purpose other than operating a legitimate dental or healthcare practice</li>
            <li>Misrepresent your identity, credentials, or the nature of your practice</li>
            <li>Use the Service to collect patient data without appropriate disclosure to patients</li>
            <li>Attempt to reverse-engineer, decompile, or extract the source code of the Service</li>
            <li>Use the Service to transmit spam, malware, or any harmful content</li>
            <li>Resell, sublicense, or otherwise commercially exploit the Service without our written permission</li>
            <li>Interfere with or disrupt the integrity or performance of the Service</li>
            <li>Circumvent any technical measures we use to protect the Service</li>
          </ul>
        </Section>

        <Section title="5. Patient Data and HIPAA">
          <p>
            The Service processes patient-provided information including names, phone numbers, email
            addresses, and appointment intent. Dental practices are independent entities responsible
            for their own compliance with the Health Insurance Portability and Accountability Act
            (HIPAA) and applicable state privacy laws.
          </p>
          <p>
            Practices that require a Business Associate Agreement (BAA) under HIPAA must contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-electric-teal underline">{CONTACT_EMAIL}</a>{' '}
            before using the Service to process protected health information (PHI). Use of the Service
            without an executed BAA, where one is required, is a violation of these Terms.
          </p>
          <p>
            By using the Service, you represent that your use complies with all applicable laws,
            including those governing patient privacy and data protection.
          </p>
        </Section>

        <Section title="6. Third-Party Integrations">
          <p>
            The Service may be connected to third-party services including Open Dental, Google Calendar,
            and email providers. Your use of those integrations is subject to the respective third
            party's terms of service. MomoroX is not responsible for the availability, accuracy, or
            conduct of any third-party services.
          </p>
          <p>
            When you connect Google Calendar, you authorize MomoroX to create calendar events on your
            behalf. This authorization can be revoked at any time from your dashboard or directly from
            your Google Account settings.
          </p>
        </Section>

        <Section title="7. Intellectual Property">
          <p>
            The Service and all content, features, and functionality (including but not limited to
            software, text, displays, images, and the design, selection, and arrangement thereof) are
            owned by MomoroX and are protected by applicable intellectual property laws.
          </p>
          <p>
            The WordPress plugin is licensed under the GNU General Public License v2 or later (GPL-2.0+).
            The plugin source code is available in the WordPress Plugin Directory and on our GitHub repository.
          </p>
          <p>
            You retain ownership of all content you provide to train the AI (e.g., your practice's
            website content and knowledge base). By submitting this content, you grant MomoroX a
            limited license to process and use it solely to provide the Service to you.
          </p>
        </Section>

        <Section title="8. Payment and Subscription">
          <p>
            Certain features of the Service may require a paid subscription. If you subscribe to a
            paid plan:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Fees are billed in advance on the applicable billing cycle (monthly or annual)</li>
            <li>All fees are non-refundable except as required by law or as stated in a specific plan</li>
            <li>We may change subscription fees with 30 days' notice; continued use constitutes acceptance</li>
            <li>Failure to pay may result in suspension or termination of your account</li>
          </ul>
          <p>
            Free plans are provided as-is without any service level commitments.
          </p>
        </Section>

        <Section title="9. Disclaimers">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS
            FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            MomoroX does not warrant that the Service will be uninterrupted, error-free, or free of
            harmful components. The AI assistant may occasionally provide inaccurate responses.
            Practices are responsible for reviewing AI-collected appointment information before
            confirming appointments with patients.
          </p>
          <p>
            MomoroX does not provide medical, legal, or dental advice. The Service is a communication
            and scheduling tool only.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, MOMOROX SHALL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF
            PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR YOUR
            USE OF THE SERVICE, EVEN IF MOMOROX HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            IN NO EVENT SHALL MOMOROX'S TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT
            YOU PAID TO MOMOROX IN THE TWELVE MONTHS PRECEDING THE CLAIM OR (B) ONE HUNDRED DOLLARS ($100).
          </p>
        </Section>

        <Section title="11. Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless MomoroX and its officers, directors,
            employees, and agents from and against any claims, liabilities, damages, losses, and
            expenses (including reasonable legal fees) arising out of or in connection with your use
            of the Service, your violation of these Terms, or your violation of any applicable law
            or third-party rights.
          </p>
        </Section>

        <Section title="12. Termination">
          <p>
            Either party may terminate the agreement governed by these Terms at any time. You may
            close your account from the dashboard or by contacting us. We may suspend or terminate
            your access immediately if you violate these Terms or if we determine your use poses a
            risk to the Service or other users.
          </p>
          <p>
            Upon termination, your right to use the Service ceases immediately. We will delete your
            account data within 30 days of a written deletion request, subject to retention
            requirements under applicable law.
          </p>
        </Section>

        <Section title="13. Changes to Terms">
          <p>
            We may modify these Terms at any time. We will notify you of material changes by email
            or by posting a notice in your dashboard at least 14 days before the changes take effect.
            Continued use of the Service after the effective date constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="14. Governing Law">
          <p>
            These Terms are governed by the laws of the State of Delaware, United States, without
            regard to its conflict of law provisions. Any dispute arising under these Terms shall
            be subject to the exclusive jurisdiction of the courts located in Delaware.
          </p>
        </Section>

        <Section title="15. Contact">
          <p>For questions about these Terms, contact us:</p>
          <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-4 text-sm space-y-1">
            <p className="text-white font-semibold">MomoroX, Inc.</p>
            <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-electric-teal underline">{CONTACT_EMAIL}</a></p>
            <p>Website: <a href="https://momorox.com" className="text-electric-teal underline">https://momorox.com</a></p>
          </div>
        </Section>

        <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between">
          <Link to="/" className="text-electric-teal text-sm hover:underline">← Back to MomoroX</Link>
          <Link to="/privacy" className="text-gray-500 text-sm hover:text-electric-teal transition-colors">Privacy Policy</Link>
          <span className="text-gray-600 text-xs">© {new Date().getFullYear()} MomoroX, Inc.</span>
        </div>
      </div>
    </div>
  );
}
